import { PorcupineWorker } from '@picovoice/porcupine-web';

/**
 * Lightweight wake word detection using Porcupine (on-device).
 * - Initializes mic stream at 16kHz mono
 * - Emits callback when the custom wake word is detected
 */
class WakeWordService {
  constructor({
    accessKey,
    keywordModel,
    sensitivity = 0.6,
    onWakeWord,
    silenceCallback,
    silenceTimeout = 5000,
    porcupineModelPublicPath = '/porcupine_params.pv',
    deviceId = null,
  }) {
    this.accessKey = accessKey; // Picovoice AccessKey
    this.keywordModel = keywordModel; // base64 or public URL to .ppn model
    this.sensitivity = sensitivity;
    this.onWakeWord = onWakeWord;
    this.silenceCallback = silenceCallback;
    this.silenceTimeout = silenceTimeout;
    this.porcupineModel = { publicPath: porcupineModelPublicPath };
    this.deviceId = deviceId;

    this.audioContext = null;
    this.processor = null;
    this.mediaStream = null;
    this.handle = null; // PorcupineWorker instance
    this.isInitialized = false;
    this.lastSpeechTimestamp = Date.now();
    this.silenceInterval = null;
    // When > 0, silence detection is suppressed until this timestamp
    this.graceUntil = 0;

    this._int16Buffer = new Int16Array(0);
  }

  async init() {
    if (this.isInitialized) return;

    // Normalize keyword into expected object
    let keyword;
    if (this.keywordModel && typeof this.keywordModel === 'string') {
      keyword =
        this.keywordModel.startsWith('data:') || this.keywordModel.length > 256
          ? {
              base64: this.keywordModel,
              label: 'Hey Comprehend',
              sensitivity: this.sensitivity,
            }
          : {
              publicPath: this.keywordModel,
              label: 'Hey Comprehend',
              sensitivity: this.sensitivity,
            };
    } else if (this.keywordModel) {
      keyword = this.keywordModel;
    } else {
      // Fallback to built-in keyword to avoid startup failure
      keyword = {
        label: 'Picovoice',
        builtin: 'Porcupine',
        sensitivity: this.sensitivity,
      };
    }

    // Create Porcupine worker instance
    this.handle = await PorcupineWorker.create(
      this.accessKey,
      [keyword],
      (detection) => {
        try {
          if (typeof this.onWakeWord === 'function') this.onWakeWord();
        } catch (e) {
          // no-op
        }
      },
      this.porcupineModel,
    );

    // Configure audio pipeline
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: 16000,
    });

    this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);
    this.processor.onaudioprocess = (event) => {
      const inputFrame = event.inputBuffer.getChannelData(0);
      // Convert Float32 [-1,1] to Int16 PCM per Porcupine expected format
      const pcm = new Int16Array(inputFrame.length);
      // Track simple energy for silence detection
      let energy = 0;
      for (let i = 0; i < inputFrame.length; i++) {
        let s = Math.max(-1, Math.min(1, inputFrame[i]));
        pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        energy += s * s;
      }
      // If energy above threshold, mark activity
      if (energy / inputFrame.length > 0.0005) {
        this.lastSpeechTimestamp = Date.now();
      }
      // Accumulate into frame size expected by Porcupine
      const frameLength = this.handle.frameLength;
      const concatenated = new Int16Array(
        this._int16Buffer.length + pcm.length,
      );
      concatenated.set(this._int16Buffer, 0);
      concatenated.set(pcm, this._int16Buffer.length);
      let offset = 0;
      while (offset + frameLength <= concatenated.length) {
        const frame = concatenated.subarray(offset, offset + frameLength);
        this.handle.process(frame);
        offset += frameLength;
      }
      this._int16Buffer = concatenated.subarray(offset);
    };

    // Note: iOS Safari doesn't support sampleRate constraint in getUserMedia,
    // so we only use channelCount and echoCancellation. The AudioContext
    // handles sample rate conversion.
    const audioConstraints = {
      channelCount: 1,
      echoCancellation: true,
    };

    // Add deviceId constraint if specified - use exact to force the selected device
    if (this.deviceId) {
      audioConstraints.deviceId = { exact: this.deviceId };
    }

    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: audioConstraints,
    });
    const source = this.audioContext.createMediaStreamSource(this.mediaStream);
    source.connect(this.processor);
    this.processor.connect(this.audioContext.destination);

    // Start silence monitor
    this.silenceInterval = setInterval(() => {
      const now = Date.now();
      // If we're in a grace period, skip silence checks
      if (this.graceUntil && now < this.graceUntil) {
        return;
      }
      if (now - this.lastSpeechTimestamp > this.silenceTimeout) {
        if (typeof this.silenceCallback === 'function') {
          this.silenceCallback();
        }
      }
    }, 1000);

    this.isInitialized = true;
  }

  getMediaStream() {
    return this.mediaStream;
  }

  getTranscript() {
    return '';
  }

  getTranscriptSinceWakeWord() {
    return '';
  }

  /**
   * Reset the silence timer (useful when manually triggering to give user time to speak)
   */
  resetSilenceTimer() {
    this.lastSpeechTimestamp = Date.now();
  }

  /**
   * Pause silence detection for a grace period, then resume.
   * Useful after wake word detection to give user time to speak their command.
   * @param {number} gracePeriod - How long to pause in milliseconds (default 5000ms)
   */
  pauseSilenceDetection(gracePeriod = 5000) {
    const now = Date.now();
    const newGraceUntil = now + gracePeriod;
    // Always extend (or set) the grace period; multiple calls just push it out
    this.graceUntil = Math.max(this.graceUntil || 0, newGraceUntil);
    // Reset the timestamp so the timeout window starts fresh once grace ends
    this.lastSpeechTimestamp = now;

    console.log(
      `Silence detection paused for ${gracePeriod}ms (until ${new Date(
        this.graceUntil,
      ).toISOString()})`,
    );
  }

  /**
   * Clear the grace period and resume silence detection immediately.
   * Call this when AI replies or calls a tool so silence timeout can kick in.
   */
  clearGracePeriod() {
    this.graceUntil = 0;
    this.lastSpeechTimestamp = Date.now();
    console.log('Grace period cleared, silence detection resumed');
  }

  /**
   * Update the silence timeout dynamically (for mode switching)
   * @param {number} timeout - New timeout in milliseconds
   */
  setSilenceTimeout(timeout) {
    this.silenceTimeout = timeout;
    console.log(`Silence timeout updated to ${timeout}ms`);
  }

  async stop() {
    // Prevent multiple stop calls
    if (!this.isInitialized) {
      return;
    }

    try {
      if (this.processor) {
        this.processor.disconnect();
        this.processor.onaudioprocess = null;
        this.processor = null;
      }
      if (this.audioContext && this.audioContext.state !== 'closed') {
        await this.audioContext.close();
        this.audioContext = null;
      }
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((t) => t.stop());
        this.mediaStream = null;
      }
      if (this.handle) {
        try {
          await this.handle.terminate();
        } catch (e) {}
        this.handle = null;
      }
      if (this.silenceInterval) {
        clearInterval(this.silenceInterval);
        this.silenceInterval = null;
      }
    } finally {
      this.isInitialized = false;
    }
  }
}

export default WakeWordService;
